import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Badge,
  Chip,
  Tooltip,
  useMediaQuery,
  useTheme,
  IconButton,
  Collapse,
  CircularProgress,
} from "@mui/material";
import {
  Search,
  QueueMusic,
  LibraryMusic,
  Inbox,
  Close,
  Menu as MenuIcon,
  Home as HomeIcon,
  MusicNote,
  ExpandMore,
  ExpandLess,
  Quiz as QuizIcon,
  Settings,
  FlashOn as FlashIcon,
  People as PeopleIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  StopCircle as EndIcon,
  LeaderboardOutlined as LeaderboardIcon,
  SportsEsports as GamesIcon,
  EmojiEvents as TrophyIcon,
  Casino as CasinoIcon,
  SettingsInputComponent as LiveHostIcon,
  LocalFireDepartment as HotSeatIcon,
  Insights as AnalyticsIcon,
  CardGiftcard as PrizeIcon,
} from "@mui/icons-material";
import io from "socket.io-client";

export const SIDEBAR_WIDTH = 280;

const navItems = [
  { id: "search", label: "Search Songs", icon: Search },
  { id: "queue", label: "Queue", icon: QueueMusic },
  { id: "playlist", label: "Private Playlist", icon: LibraryMusic },
  { id: "requests", label: "Requests", icon: Inbox },
];

const prizeNavItem = { id: "prize-palette", label: "Prize Palette", icon: PrizeIcon };

const quizNavItems = [
  { id: "quiz-config", label: "Config", icon: Settings },
];

const gamesNavItems = [
  { id: "higher-lower-analytics", label: "Analytics", icon: AnalyticsIcon },
  { id: "higher-lower-config", label: "Configuration", icon: Settings },
  { id: "higher-lower-pwh", label: "Play with Host", icon: LiveHostIcon },
];

const spinWheelNavItems = [
  { id: "spin-wheel-normal", label: "Normal", icon: CasinoIcon },
  { id: "spin-wheel-live-host", label: "Users Wheel", icon: LiveHostIcon },
  { id: "spin-wheel-hot-seat", label: "Hot Seat", icon: HotSeatIcon },
];

const beerillionerNavItems = [
  { id: "beerillioner-config", label: "Config", icon: Settings },
  { id: "beerillioner-host", label: "Host", icon: PlayIcon },
  { id: "beerillioner-history", label: "History", icon: LeaderboardIcon },
];

const getRapidStatusColor = (status) => {
  switch (status) {
    case "waiting": return "#FFC107";
    case "question_active": return "#4CAF50";
    case "ended": return "#F44336";
    default: return "#9E9E9E";
  }
};

const Sidebar = ({
  open,
  onToggle,
  activeTab,
  onTabChange,
  queueCount,
  requestCount,
  onViewQuizDetails,
  onEditQuizSession,
  onViewParticipants,
  onViewQuizletRankings,
  onEndFlashQuizSession,
  onToggleSessionStatus,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [jukeboxExpanded, setJukeboxExpanded] = useState(true);
  const [quizExpanded, setQuizExpanded] = useState(false);
  const [gamesExpanded, setGamesExpanded] = useState(false);
  const [spinWheelExpanded, setSpinWheelExpanded] = useState(false);
  const [beerillionerExpanded, setBeerillionerExpanded] = useState(false);
  const [activeSessions, setActiveSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const sidebarSocketRef = useRef(null);

  const fetchActiveSessions = useCallback(async () => {
    try {
      setLoadingSessions(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/live-quiz/flash/sessions`
      );
      if (!res.ok) return;
      const data = await res.json();
      const active = (data.sessions || []).filter((s) => s.status === "active");
      setActiveSessions(active);
    } catch (err) {
      console.error("Error fetching active sessions:", err);
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    fetchActiveSessions();

    const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";
    const socket = io(baseUrl, { transports: ["websocket"] });
    sidebarSocketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("flash:join-global");
    });

    const refreshOnEvent = () => fetchActiveSessions();
    socket.on("flash:activated", refreshOnEvent);
    socket.on("flash:session_ended", refreshOnEvent);
    socket.on("flash:question_pushed", refreshOnEvent);
    socket.on("flash:question_stopped", refreshOnEvent);
    socket.on("flash:participants", (payload) => {
      if (payload?.session_id) {
        setActiveSessions((prev) =>
          prev.map((s) =>
            s.session_id === payload.session_id
              ? { ...s, participant_count: payload.participants?.length || s.participant_count }
              : s
          )
        );
      }
    });
    socket.on("flash:leaderboard", (payload) => {
      if (payload?.session_id) {
        setActiveSessions((prev) =>
          prev.map((s) =>
            s.session_id === payload.session_id
              ? { ...s, participant_count: payload.leaderboard?.length || s.participant_count }
              : s
          )
        );
      }
    });

    return () => {
      if (sidebarSocketRef.current) {
        sidebarSocketRef.current.emit("flash:leave-global");
        sidebarSocketRef.current.disconnect();
        sidebarSocketRef.current = null;
      }
    };
  }, [fetchActiveSessions]);

  useEffect(() => {
    if (isMobile && open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile, open]);

  const handleNavClick = (tabId) => {
    onTabChange(tabId);
    if (isMobile) onToggle();
  };

  const isHomeActive = activeTab === "home";
  const isPrizeActive = activeTab === prizeNavItem.id;
  const isJukeboxActive = navItems.some((n) => n.id === activeTab);
  const isQuizActive = quizNavItems.some((n) => n.id === activeTab);
  const isGamesActive = gamesNavItems.some((n) => n.id === activeTab);
  const isSpinWheelActive = spinWheelNavItems.some((n) => n.id === activeTab);
  const isBeerillionerActive = beerillionerNavItems.some((n) => n.id === activeTab);

  return (
    <>
      {isMobile && open && (
        <Box
          onClick={onToggle}
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            zIndex: 1199,
            transition: "opacity 0.3s",
          }}
        />
      )}

      <Box
        sx={{
          position: isMobile ? "fixed" : "relative",
          top: 0,
          left: 0,
          width: open ? SIDEBAR_WIDTH : 0,
          minWidth: open ? SIDEBAR_WIDTH : 0,
          height: isMobile ? "100vh" : "100%",
          backgroundColor: "#0A0A0A",
          borderRight: open ? "1px solid rgba(255,255,255,0.06)" : "none",
          transition:
            "width 0.3s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          overflow: "hidden",
          zIndex: isMobile ? 1200 : 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header: menu toggle */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            px: 1.5,
            py: 1,
            flexShrink: 0,
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <IconButton
            size="small"
            onClick={onToggle}
            sx={{ color: "rgba(255,255,255,0.55)" }}
          >
            {isMobile ? <Close fontSize="small" /> : <MenuIcon fontSize="small" />}
          </IconButton>
        </Box>

        {/* Nav section */}
        <Box sx={{ flex: 1, py: 1.5, overflow: "auto" }}>
          {/* Home link */}
          <Box
            onClick={() => handleNavClick("home")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1.3,
              mx: 1,
              mb: 0.5,
              borderRadius: 1.5,
              cursor: "pointer",
              backgroundColor: isHomeActive
                ? "rgba(29,185,84,0.12)"
                : "transparent",
              color: isHomeActive ? "#1DB954" : "rgba(255,255,255,0.45)",
              transition: "all 0.15s",
              "&:hover": {
                backgroundColor: isHomeActive
                  ? "rgba(29,185,84,0.12)"
                  : "rgba(255,255,255,0.04)",
                color: isHomeActive ? "#1DB954" : "rgba(255,255,255,0.65)",
              },
            }}
          >
            <HomeIcon sx={{ fontSize: 22 }} />
            <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: isHomeActive ? 700 : 600,
                whiteSpace: "nowrap",
                letterSpacing: -0.3,
              }}
            >
              Home
            </Typography>
          </Box>

          {/* Prize Palette */}
          <Box
            onClick={() => handleNavClick(prizeNavItem.id)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1.3,
              mx: 1,
              mb: 0.5,
              borderRadius: 1.5,
              cursor: "pointer",
              backgroundColor: isPrizeActive
                ? "rgba(255,213,79,0.14)"
                : "transparent",
              color: isPrizeActive ? "#FFD54F" : "rgba(255,255,255,0.45)",
              transition: "all 0.15s",
              "&:hover": {
                backgroundColor: isPrizeActive
                  ? "rgba(255,213,79,0.14)"
                  : "rgba(255,255,255,0.04)",
                color: isPrizeActive ? "#FFD54F" : "rgba(255,255,255,0.65)",
              },
            }}
          >
            <PrizeIcon sx={{ fontSize: 22 }} />
            <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: isPrizeActive ? 700 : 600,
                whiteSpace: "nowrap",
                letterSpacing: -0.3,
              }}
            >
              {prizeNavItem.label}
            </Typography>
          </Box>

          {/* Jukebox accordion link */}
          <Box
            onClick={() => setJukeboxExpanded((p) => !p)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1.3,
              mx: 1,
              borderRadius: 1.5,
              cursor: "pointer",
              backgroundColor: isJukeboxActive
                ? "rgba(29,185,84,0.08)"
                : "transparent",
              transition: "all 0.15s",
              "&:hover": {
                backgroundColor: isJukeboxActive
                  ? "rgba(29,185,84,0.08)"
                  : "rgba(255,255,255,0.04)",
              },
            }}
          >
            <MusicNote
              sx={{
                fontSize: 22,
                color: isJukeboxActive ? "#1DB954" : "rgba(255,255,255,0.5)",
              }}
            />
            <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: 700,
                color: isJukeboxActive ? "#1DB954" : "rgba(255,255,255,0.7)",
                flex: 1,
                whiteSpace: "nowrap",
                letterSpacing: -0.3,
              }}
            >
              Jukebox
            </Typography>
            {jukeboxExpanded ? (
              <ExpandLess
                sx={{
                  fontSize: 18,
                  color: "rgba(255,255,255,0.3)",
                }}
              />
            ) : (
              <ExpandMore
                sx={{
                  fontSize: 18,
                  color: "rgba(255,255,255,0.3)",
                }}
              />
            )}
          </Box>

          {/* Accordion children */}
          <Collapse in={jukeboxExpanded}>
            <Box sx={{ pt: 0.5 }}>
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <Box
                    key={item.id}
                    onClick={() => handleNavClick(item.id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      pl: 4.5,
                      pr: 2,
                      py: 1,
                      mx: 1,
                      borderRadius: 1.5,
                      cursor: "pointer",
                      backgroundColor: isActive
                        ? "rgba(29,185,84,0.12)"
                        : "transparent",
                      color: isActive ? "#1DB954" : "rgba(255,255,255,0.45)",
                      transition: "all 0.15s",
                      "&:hover": {
                        backgroundColor: isActive
                          ? "rgba(29,185,84,0.12)"
                          : "rgba(255,255,255,0.04)",
                        color: isActive
                          ? "#1DB954"
                          : "rgba(255,255,255,0.65)",
                      },
                    }}
                  >
                    {(item.id === "queue" && queueCount > 0) || (item.id === "requests" && requestCount > 0) ? (
                      <Badge
                        badgeContent={item.id === "queue" ? queueCount : requestCount}
                        color={item.id === "requests" ? "warning" : "success"}
                        max={99}
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: "0.55rem",
                            height: 15,
                            minWidth: 15,
                            backgroundColor: item.id === "requests" ? "#FFA726" : "#1DB954",
                          },
                        }}
                      >
                        <Icon sx={{ fontSize: 18 }} />
                      </Badge>
                    ) : (
                      <Icon sx={{ fontSize: 18 }} />
                    )}
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: isActive ? 600 : 400,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Collapse>

          {/* Quiz accordion */}
          <Box
            onClick={() => setQuizExpanded((p) => !p)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1.3,
              mx: 1,
              mt: 0.5,
              borderRadius: 1.5,
              cursor: "pointer",
              backgroundColor: isQuizActive
                ? "rgba(29,185,84,0.08)"
                : "transparent",
              transition: "all 0.15s",
              "&:hover": {
                backgroundColor: isQuizActive
                  ? "rgba(29,185,84,0.08)"
                  : "rgba(255,255,255,0.04)",
              },
            }}
          >
            <QuizIcon
              sx={{
                fontSize: 22,
                color: isQuizActive ? "#1DB954" : "rgba(255,255,255,0.5)",
              }}
            />
            <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: 700,
                color: isQuizActive ? "#1DB954" : "rgba(255,255,255,0.7)",
                flex: 1,
                whiteSpace: "nowrap",
                letterSpacing: -0.3,
              }}
            >
              Quiz
            </Typography>
            {quizExpanded ? (
              <ExpandLess
                sx={{
                  fontSize: 18,
                  color: "rgba(255,255,255,0.3)",
                }}
              />
            ) : (
              <ExpandMore
                sx={{
                  fontSize: 18,
                  color: "rgba(255,255,255,0.3)",
                }}
              />
            )}
          </Box>

          <Collapse in={quizExpanded}>
            <Box sx={{ pt: 0.5 }}>
              {quizNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <Box
                    key={item.id}
                    onClick={() => handleNavClick(item.id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      pl: 4.5,
                      pr: 2,
                      py: 1,
                      mx: 1,
                      borderRadius: 1.5,
                      cursor: "pointer",
                      backgroundColor: isActive
                        ? "rgba(29,185,84,0.12)"
                        : "transparent",
                      color: isActive ? "#1DB954" : "rgba(255,255,255,0.45)",
                      transition: "all 0.15s",
                      "&:hover": {
                        backgroundColor: isActive
                          ? "rgba(29,185,84,0.12)"
                          : "rgba(255,255,255,0.04)",
                        color: isActive
                          ? "#1DB954"
                          : "rgba(255,255,255,0.65)",
                      },
                    }}
                  >
                    <Icon sx={{ fontSize: 18 }} />
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: isActive ? 600 : 400,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                );
              })}

              {/* Active Insta Quiz Cards */}
              {loadingSessions ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                  <CircularProgress size={16} sx={{ color: "#9C27B0" }} />
                </Box>
              ) : (
                activeSessions.map((session) => {
                  const rapidStatus = session.rapid_status || "waiting";
                  return (
                    <Box
                      key={session.session_id}
                      sx={{
                        mx: 1.5,
                        mt: 1,
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: "rgba(156, 39, 176, 0.08)",
                        border: "1px solid rgba(156, 39, 176, 0.3)",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: "rgba(156, 39, 176, 0.6)",
                          backgroundColor: "rgba(156, 39, 176, 0.12)",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                        <FlashIcon sx={{ fontSize: 16, color: "#9C27B0" }} />
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "#fff",
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {session.quiz_name}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                        <Chip
                          label="Active"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            backgroundColor: "rgba(156, 39, 176, 0.2)",
                            color: "#CE93D8",
                            border: "1px solid rgba(156, 39, 176, 0.5)",
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                        <Chip
                          label={rapidStatus}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            backgroundColor: `${getRapidStatusColor(rapidStatus)}15`,
                            color: getRapidStatusColor(rapidStatus),
                            border: `1px solid ${getRapidStatusColor(rapidStatus)}50`,
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.75 }}>
                        <PeopleIcon sx={{ fontSize: 13, color: "rgba(255,255,255,0.4)" }} />
                        <Typography sx={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.5)" }}>
                          {session.participant_count || 0} participants
                        </Typography>
                      </Box>

                      {/* Action buttons */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.25,
                          mt: 1,
                          pt: 0.75,
                          borderTop: "1px solid rgba(255,255,255,0.06)",
                          flexWrap: "wrap",
                          justifyContent: "center",
                        }}
                      >
                        <Tooltip title="View Details" arrow>
                          <IconButton
                            size="small"
                            onClick={() => onViewQuizDetails?.(session)}
                            sx={{ color: "#9C27B0", p: 0.5 }}
                          >
                            <ViewIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit" arrow>
                          <IconButton
                            size="small"
                            onClick={() => onEditQuizSession?.(session)}
                            sx={{ color: "#4CAF50", p: 0.5 }}
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Participants" arrow>
                          <IconButton
                            size="small"
                            onClick={() => onViewParticipants?.(session)}
                            sx={{ color: "#2196F3", p: 0.5 }}
                          >
                            <PeopleIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Quizlet Rankings" arrow>
                          <IconButton
                            size="small"
                            onClick={() => onViewQuizletRankings?.(session)}
                            sx={{ color: "#FF9800", p: 0.5 }}
                          >
                            <LeaderboardIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="End Session" arrow>
                          <IconButton
                            size="small"
                            onClick={() => onEndFlashQuizSession?.(session.session_id)}
                            sx={{ color: "#F44336", p: 0.5 }}
                          >
                            <EndIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Deactivate" arrow>
                          <IconButton
                            size="small"
                            onClick={() => onToggleSessionStatus?.(session.session_id, session.status)}
                            sx={{ color: "#F44336", p: 0.5 }}
                          >
                            <StopIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          </Collapse>

          {/* Spin Wheel accordion */}
          <Box
            onClick={() => setSpinWheelExpanded((p) => !p)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1.3,
              mx: 1,
              mt: 0.5,
              borderRadius: 1.5,
              cursor: "pointer",
              backgroundColor: isSpinWheelActive
                ? "rgba(29,185,84,0.08)"
                : "transparent",
              transition: "all 0.15s",
              "&:hover": {
                backgroundColor: isSpinWheelActive
                  ? "rgba(29,185,84,0.08)"
                  : "rgba(255,255,255,0.04)",
              },
            }}
          >
            <CasinoIcon
              sx={{
                fontSize: 22,
                color: isSpinWheelActive ? "#1DB954" : "rgba(255,255,255,0.5)",
              }}
            />
            <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: 700,
                color: isSpinWheelActive ? "#1DB954" : "rgba(255,255,255,0.7)",
                flex: 1,
                whiteSpace: "nowrap",
                letterSpacing: -0.3,
              }}
            >
              Spin Wheel
            </Typography>
            {spinWheelExpanded ? (
              <ExpandLess
                sx={{
                  fontSize: 18,
                  color: "rgba(255,255,255,0.3)",
                }}
              />
            ) : (
              <ExpandMore
                sx={{
                  fontSize: 18,
                  color: "rgba(255,255,255,0.3)",
                }}
              />
            )}
          </Box>

          <Collapse in={spinWheelExpanded}>
            <Box sx={{ pt: 0.5 }}>
              {spinWheelNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <Box
                    key={item.id}
                    onClick={() => handleNavClick(item.id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      pl: 4.5,
                      pr: 2,
                      py: 1,
                      mx: 1,
                      borderRadius: 1.5,
                      cursor: "pointer",
                      backgroundColor: isActive
                        ? "rgba(29,185,84,0.12)"
                        : "transparent",
                      color: isActive ? "#1DB954" : "rgba(255,255,255,0.45)",
                      transition: "all 0.15s",
                      "&:hover": {
                        backgroundColor: isActive
                          ? "rgba(29,185,84,0.12)"
                          : "rgba(255,255,255,0.04)",
                        color: isActive
                          ? "#1DB954"
                          : "rgba(255,255,255,0.65)",
                      },
                    }}
                  >
                    <Icon sx={{ fontSize: 18 }} />
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: isActive ? 600 : 400,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Collapse>

          {/* Higher & Lower accordion */}
          <Box
            onClick={() => setGamesExpanded((p) => !p)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1.3,
              mx: 1,
              mt: 0.5,
              borderRadius: 1.5,
              cursor: "pointer",
              backgroundColor: isGamesActive
                ? "rgba(29,185,84,0.08)"
                : "transparent",
              transition: "all 0.15s",
              "&:hover": {
                backgroundColor: isGamesActive
                  ? "rgba(29,185,84,0.08)"
                  : "rgba(255,255,255,0.04)",
              },
            }}
          >
            <GamesIcon
              sx={{
                fontSize: 22,
                color: isGamesActive ? "#1DB954" : "rgba(255,255,255,0.5)",
              }}
            />
            <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: 700,
                color: isGamesActive ? "#1DB954" : "rgba(255,255,255,0.7)",
                flex: 1,
                whiteSpace: "nowrap",
                letterSpacing: -0.3,
              }}
            >
              Higher & Lower
            </Typography>
            {gamesExpanded ? (
              <ExpandLess
                sx={{
                  fontSize: 18,
                  color: "rgba(255,255,255,0.3)",
                }}
              />
            ) : (
              <ExpandMore
                sx={{
                  fontSize: 18,
                  color: "rgba(255,255,255,0.3)",
                }}
              />
            )}
          </Box>

          <Collapse in={gamesExpanded}>
            <Box sx={{ pt: 0.5 }}>
              {gamesNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <Box
                    key={item.id}
                    onClick={() => handleNavClick(item.id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      pl: 4.5,
                      pr: 2,
                      py: 1,
                      mx: 1,
                      borderRadius: 1.5,
                      cursor: "pointer",
                      backgroundColor: isActive
                        ? "rgba(29,185,84,0.12)"
                        : "transparent",
                      color: isActive ? "#1DB954" : "rgba(255,255,255,0.45)",
                      transition: "all 0.15s",
                      "&:hover": {
                        backgroundColor: isActive
                          ? "rgba(29,185,84,0.12)"
                          : "rgba(255,255,255,0.04)",
                        color: isActive
                          ? "#1DB954"
                          : "rgba(255,255,255,0.65)",
                      },
                    }}
                  >
                    <Icon sx={{ fontSize: 18 }} />
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: isActive ? 600 : 400,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Collapse>

          {/* Beerillioner accordion */}
          <Box
            onClick={() => setBeerillionerExpanded((p) => !p)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1.3,
              mx: 1,
              mt: 0.5,
              borderRadius: 1.5,
              cursor: "pointer",
              backgroundColor: isBeerillionerActive
                ? "rgba(29,185,84,0.08)"
                : "transparent",
              transition: "all 0.15s",
              "&:hover": {
                backgroundColor: isBeerillionerActive
                  ? "rgba(29,185,84,0.08)"
                  : "rgba(255,255,255,0.04)",
              },
            }}
          >
            <TrophyIcon
              sx={{
                fontSize: 22,
                color: isBeerillionerActive ? "#1DB954" : "rgba(255,255,255,0.5)",
              }}
            />
            <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: 700,
                color: isBeerillionerActive ? "#1DB954" : "rgba(255,255,255,0.7)",
                flex: 1,
                whiteSpace: "nowrap",
                letterSpacing: -0.3,
              }}
            >
              Beerillioner
            </Typography>
            {beerillionerExpanded ? (
              <ExpandLess
                sx={{
                  fontSize: 18,
                  color: "rgba(255,255,255,0.3)",
                }}
              />
            ) : (
              <ExpandMore
                sx={{
                  fontSize: 18,
                  color: "rgba(255,255,255,0.3)",
                }}
              />
            )}
          </Box>

          <Collapse in={beerillionerExpanded}>
            <Box sx={{ pt: 0.5 }}>
              {beerillionerNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <Box
                    key={item.id}
                    onClick={() => handleNavClick(item.id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      pl: 4.5,
                      pr: 2,
                      py: 1,
                      mx: 1,
                      borderRadius: 1.5,
                      cursor: "pointer",
                      backgroundColor: isActive
                        ? "rgba(29,185,84,0.12)"
                        : "transparent",
                      color: isActive ? "#1DB954" : "rgba(255,255,255,0.45)",
                      transition: "all 0.15s",
                      "&:hover": {
                        backgroundColor: isActive
                          ? "rgba(29,185,84,0.12)"
                          : "rgba(255,255,255,0.04)",
                        color: isActive
                          ? "#1DB954"
                          : "rgba(255,255,255,0.65)",
                      },
                    }}
                  >
                    <Icon sx={{ fontSize: 18 }} />
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: isActive ? 600 : 400,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Collapse>
        </Box>
      </Box>
    </>
  );
};

export default Sidebar;
